// ⚠️ Uses dockview internals (`api.component.gridview`, `createGroupAtLocation`), verified on 8.4.0.
const MIN_SIZE = 220

/* Every divider in the main grid with a branch on at least one side; between two plain groups a native edge drop already does the same. */
export function computeZones(api, container) {
	const root = api.component?.gridview?.root
	if (!root) return []
	const origin = container.getBoundingClientRect()
	const zones = []
	const walk = (node, path) => {
		if (!node.children) return
		const horizontal = node.orientation === 'HORIZONTAL'
		const box = node.element.getBoundingClientRect()
		node.children.forEach((child, i) => {
			walk(child, [...path, i])
			const next = node.children[i + 1]
			if (!next || (!child.children && !next.children)) return
			// A full-width row, which the layout's edges refuse too.
			if (node === root && !horizontal) return
			const a = child.element.getBoundingClientRect()
			const b = next.element.getBoundingClientRect()
			if (!a.width || !a.height || !b.width || !b.height) return
			zones.push({
				key: [...path, i].join('.'),
				branch: node,
				location: [...path, i + 1],
				horizontal,
				extent: horizontal ? box.width : box.height,
				bounds: { x: box.left - origin.left, y: box.top - origin.top, w: box.width, h: box.height },
				rect: horizontal
					? { x: b.left - origin.left, y: box.top - origin.top, w: 0, h: box.height }
					: { x: box.left - origin.left, y: b.top - origin.top, w: box.width, h: 0 },
			})
		})
	}
	walk(root, [])
	return zones
}

export const parentOf = (node, view) => {
	if (!node.children) return null
	for (const child of node.children) {
		if (child.view === view) return node
		const hit = parentOf(child, view)
		if (hit) return hit
	}
	return null
}

// A leaf is keyed by its group: dockview rebuilds leaf nodes when it wraps one in a new branch.
const keyOf = node => node.view ?? node

const walkBranches = (node, fn) => {
	if (!node?.children) return
	fn(node)
	for (const child of node.children) walkBranches(child, fn)
}

/* Every grid child's size along its branch's axis, taken before a drop. */
export function snapshotSizes(api) {
	const sizes = new Map()
	walkBranches(api.component?.gridview?.root, branch => {
		const horizontal = branch.orientation === 'HORIZONTAL'
		branch.children.forEach((c, i) => sizes.set(keyOf(c), { size: branch.splitview.getViewSize(i), horizontal }))
	})
	return sizes
}

// Kept children return to their old size, new ones keep theirs, all within constraints; the widest kept ones absorb the rest, then the widest new ones (a branch the drop made has no kept child).
function settleBranch(branch, before) {
	const sv = branch.splitview
	const items = sv.viewItems
	const horizontal = branch.orientation === 'HORIZONTAL'
	const fit = (s, i) => Math.min(Math.max(s, items[i].minimumSize), items[i].maximumSize)
	const prior = branch.children.map(c => {
		const was = before.get(keyOf(c))
		return was?.horizontal === horizontal ? was.size : undefined
	})
	const sizes = prior.map((s, i) => fit(s ?? sv.getViewSize(i), i))
	let rest = sv.size - sizes.reduce((a, b) => a + b, 0)
	const kept = i => prior[i] !== undefined
	const absorbers = sizes.map((_, i) => i).sort((a, b) => kept(b) - kept(a) || sizes[b] - sizes[a])
	for (const i of absorbers) {
		const s = fit(sizes[i] + rest, i)
		rest -= s - sizes[i]
		sizes[i] = s
	}
	if (Math.abs(rest) >= 1 || sizes.every((s, i) => s === sv.getViewSize(i))) return
	sizes.forEach((s, i) => {
		items[i].size = s
	})
	sv.relayout([], [])
}

// ⚠️ dockview halves the group a drop splits without clamping to the mover's maximum width. `also` is a column the drop inserted into.
export function settleSizes(api, before, also) {
	walkBranches(api.component?.gridview?.root, branch => {
		if (branch.orientation === 'HORIZONTAL' || branch === also) settleBranch(branch, before)
	})
}

const sourceOf = dragged => dragged.group ?? dragged.panel?.group

/* The new slot's extent along the zone's axis. */
export function slotSize(zone, dragged) {
	const source = sourceOf(dragged)
	const extent = zone.horizontal ? source?.width : source?.height
	return Math.round(Math.min(Math.max(extent || MIN_SIZE, MIN_SIZE), Math.max(MIN_SIZE, zone.extent * 0.35)))
}

/* `dragged` is `{ panel }` (a tab) or `{ group }` (a group header). */
export function dropIntoZone(api, zone, dragged) {
	if (!sourceOf(dragged)) return
	const before = snapshotSizes(api)
	const target = api.component.createGroupAtLocation(zone.location, slotSize(zone, dragged))
	if (dragged.panel) dragged.panel.api.moveTo({ group: target, position: 'center' })
	else dragged.group.api.moveTo({ group: target, position: 'center' })
	if (target.panels.length === 0) {
		api.removeGroup(target)
		return
	}
	settleSizes(api, before, parentOf(api.component.gridview.root, target))
	target.api.setActive()
}
